import {
  CommonHelperFunctions,
  EWorkspaceProjectRole,
  IChangePasswordDto,
  IExternalTokenRequest,
  IForgotPasswordDto,
  IInvitationDto,
  ILoginDto,
  IResetPasswordDto,
  IResetPasswordWithTokenDto,
  IRoleDto,
  ISignupDto,
  ISubscriptionDto,
  IUserDto,
  IUserInfoDto,
  IVerifyVerificationCodeDto,
  RoleItem
} from "@kuika/kuika-cl-model";
import { IAssignWorkspaceRoleToUserRequest } from "@kuika/kuika-cl-model/src/dtos/user-info.dto";
import { IWorkspaceRoleDto } from "@kuika/kuika-cl-model/src/dtos/workspace-role.dto";
import Axios, { AxiosResponse } from "axios";
import LocalStorageService from "../auth/local-storage-service";
import { syncRememberedAccountPhoto } from "../auth/remembered-accounts";
import { InvitationResponseDTO, RespondInvitationDTO } from "../components/authentication/invitation";
import { FireBaseService } from "../firebase/firebase";
import confJson from "../shared/data/conf.json";
import { KMainFunctions } from "../shared/utility/main-functions";

const backendUrl = CommonHelperFunctions.GetBackendUrl();

const redirectDomains = confJson.redirectDomainToUrl;

declare let window: any;
export class UserService {
  private static async ProcessLoginResult(res: AxiosResponse<any>): Promise<AxiosResponse> {
    if (res && res.status === 200) {
      localStorage.setItem("tokenString", res.data.tokenString);
      localStorage.setItem("refreshToken", res.data.refreshToken);
      if (window.userInfo === undefined) {
        await UserService.GetUserInfo();
      }
    }
    return res;
  }

  public static Invite(invitation: IInvitationDto): Promise<AxiosResponse> {
    try {
      return Axios.post<IInvitationDto, AxiosResponse>(`${backendUrl}/users/invite`, invitation);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static AcceptInvite(payload: RespondInvitationDTO) {
    return Axios.post<RespondInvitationDTO, AxiosResponse<InvitationResponseDTO>>(
      `${backendUrl}/users/acceptInvitation`,
      payload
    );
  }

  public static RejectInvite(payload: RespondInvitationDTO) {
    return Axios.post<RespondInvitationDTO, AxiosResponse<InvitationResponseDTO>>(
      `${backendUrl}/users/rejectInvitation`,
      payload
    );
  }

  public static async Create(user: IUserDto): Promise<AxiosResponse> {
    try {
      return await Axios.post<IUserDto, AxiosResponse>(`${backendUrl}/users/create`, user);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async Login(user: ILoginDto, isHTML: boolean = false): Promise<string | number> {
    try {
      const res = await Axios.post<ILoginDto, AxiosResponse>(`${backendUrl}/auth/login`, user);
      if (res && res.status === 200) {
        if (res.data.status !== 0) {
          return res.data.status;
        }
        LocalStorageService.setToken(res.data);
        await this.GetUserInfo(isHTML);
        const firebase = new FireBaseService();
        firebase.logEvent("K_sign_in", {
          type: "platform",
          username: window.userInfo?.username,
          id: window.userInfo?.id
        });
        firebase.setUserId(window.userInfo?.id);
      }
      return "";
    } catch (error: any) {
      // KMainFunctions.exceptionHandler(error);
      if (error) {
        if (typeof error === "string") return error;
        if (error.response && error.response.data) return error.response.data.message;
        if (error.message) return error.message;
      }

      return "An error has been occurred. Please try again.";
    }
  }

  public static async SignUp(user: ISignupDto): Promise<string> {
    try {
      await Axios.post<ISignupDto, AxiosResponse>(`${backendUrl}/users/create`, user);
      return "";
    } catch (error: any) {
      // KMainFunctions.exceptionHandler(error);
      if (error && error.response && error.response.data) {
        return error.response.data.message;
      }
      return "An error has been occurred. Please try again.";
    }
  }

  public static async GetUserInfo(isHTML: boolean = false): Promise<IUserInfoDto> {
    if (!window?.userInfo?.username) {
      try {
        const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/auth/userinfo`);
        if (res && res.status === 200) {
          if (res?.data?.userLogoutMessage?.length > 0) {
            setTimeout(() => {
              this.Logout();
            }, 3000);

            if (isHTML) throw new Error(res.data.userLogoutMessage);
            else KMainFunctions.displayWarningNotification(res?.data?.userLogoutMessage);
          } else {
            window.userInfo = res.data as IUserInfoDto;
            // Update-only: refreshes the photo of an account this device already remembers
            // (changed on another device, or remembered before photos were kept). Never adds one.
            syncRememberedAccountPhoto(window.userInfo.username, window.userInfo.profilePhotoUrl);
            LocalStorageService.setCurrentDomain(res.data.lastUsedDomainName);
            if (typeof window.initProductFruits === "function") {
              window.initProductFruits(window.userInfo);
            }
            window.usetifulTags = {
              userId: window.userInfo?.id,
              firstName: window.userInfo?.firstName,
              lastName: window.userInfo?.lastName
            };
          }
        }
      } catch (error: any) {
        window.userInfo = undefined;
        if (isHTML) throw error;
        KMainFunctions.exceptionHandler(error);
        return error;
      }
    }
    this.RedirectIfRequired();
    return window.userInfo as IUserInfoDto;
  }

  public static async GetUserById(userId: string): Promise<IUserInfoDto> {
    try {
      const res = await Axios.get<IUserInfoDto, AxiosResponse>(`${backendUrl}/users/${userId}/detail`);
      return res.data;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  static async RedirectIfRequired() {
    const domainId = window.userInfo?.kDomain_Id;
    if (domainId) {
      const r = redirectDomains.find((x) => x.from.toUpperCase() === domainId.toUpperCase());
      if (r) {
        let toUrl = r.to;
        if (toUrl !== window.location.origin) {
          const refreshToken = LocalStorageService.getRefreshToken();
          localStorage.removeItem("refreshToken");
          if (refreshToken) {
            toUrl += `/redirect?token=${refreshToken}`;
          }
          window.location.assign(toUrl);
        }
      }
    }
  }

  public static async GetUserInfoForce(): Promise<any> {
    try {
      const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/auth/userinfo`);
      if (res && res.status === 200) {
        if (res?.data?.userLogoutMessage?.length > 0) {
          KMainFunctions.displayWarningNotification(res?.data?.userLogoutMessage);
          setTimeout(() => {
            this.Logout();
          }, 3000);
          return;
        }
        window.userInfo = res.data;
        LocalStorageService.setCurrentDomain(res.data.lastUsedDomainName);
        if (typeof window.initProductFruits === "function") {
          window.initProductFruits(window.userInfo);
        }
        window.usetifulTags = {
          userId: window.userInfo?.id,
          firstName: window.userInfo?.firstName,
          lastName: window.userInfo?.lastName
        };
        return window.userInfo;
      }
    } catch (error: any) {
      window.userInfo = undefined;
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async ForgotPassword(user: IForgotPasswordDto): Promise<AxiosResponse> {
    try {
      LocalStorageService.setForgotPasswordEmail(user.Email);
      return await Axios.post<ILoginDto, AxiosResponse>(`${backendUrl}/auth/forgotpassword`, user);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async ResetPassword(user: IResetPasswordDto): Promise<AxiosResponse> {
    try {
      return await Axios.post<ILoginDto, AxiosResponse>(`${backendUrl}/auth/resetpasswordwithtoken`, user);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async ResetPasswordWithToken(user: IResetPasswordWithTokenDto): Promise<AxiosResponse> {
    try {
      return await Axios.post<ILoginDto, AxiosResponse>(`${backendUrl}/auth/resetpasswordwithtoken`, user);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static getEmailToResetPassword(): any {
    return LocalStorageService.getForgotPasswordEmail();
  }

  public static async Logout(sdId?: string, skipConfirmation?: boolean) {
    if (!skipConfirmation) {
      const confirm = window.confirm("Are you sure you want to logout?");
      if (!confirm) return;
    }

    try {
      const firebase = new FireBaseService();
      firebase.logEvent("sign_out", {
        username: window.userInfo?.username,
        id: window.userInfo?.id
      });
      if (sdId?.length && sdId?.length > 2) {
        await Axios.get<string, AxiosResponse>(`${backendUrl}/screendesign/leave/${sdId}`);
      }
      await Axios.post<ILoginDto, AxiosResponse>(`${backendUrl}/auth/logout`);
      await KMainFunctions.clearIndexedDbAutosaveData();
      LocalStorageService.clearToken();
      window.userInfo = undefined;
      window.skipAlert = true;
      window.location.href = "/login";
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
    }
  }

  public static async GetActiveScreenUserRoles(screen: string) {
    if (window.userInfo) {
      try {
        const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/${screen}/getmyroles`);
        if (res && res.status === 200) {
          window.userInfo.roles = res.data as RoleItem[];
        }
      } catch (error: any) {
        KMainFunctions.exceptionHandler(error);
        return error;
      }
    }
  }

  public static async AssignWorkspaceRoleToUser(obj: IAssignWorkspaceRoleToUserRequest): Promise<AxiosResponse> {
    try {
      return await Axios.put<IAssignWorkspaceRoleToUserRequest, AxiosResponse>(
        `${backendUrl}/authorization/roles-assignment`,
        obj
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async InitiateWorkspaceOwnershipTransfer(toUserId: string): Promise<AxiosResponse> {
    try {
      return await Axios.post<IAssignWorkspaceRoleToUserRequest, AxiosResponse>(
        `${backendUrl}/authorization/workspace-ownership-transfer`,
        {
          toUserId
        }
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async ProjectOwnershipTransfer(
    oldOwnerId: string,
    newOwnerId: string,
    projectId: string,
    oldOwnerNewProjectRole: EWorkspaceProjectRole
  ): Promise<AxiosResponse> {
    try {
      return await Axios.put<any, AxiosResponse>(`${backendUrl}/authorization/project-ownership-transfer`, {
        oldOwnerId,
        newOwnerId,
        projectId,
        oldOwnerNewProjectRole
      });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async RemoveUserFromProject(userId: string, projectId: string): Promise<AxiosResponse> {
    try {
      return await Axios.put<any, AxiosResponse>(`${backendUrl}/authorization/remove-user-from-project`, {
        userId,
        projectId
      });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async AssignProjectRoleToUser(
    userId: string,
    projectId: string,
    projectRoleEnumId: EWorkspaceProjectRole
  ): Promise<AxiosResponse> {
    try {
      return await Axios.put<any, AxiosResponse>(`${backendUrl}/authorization/projects/roles-assignment`, {
        userId,
        projectId,
        projectRoleEnumId
      });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetWorkspaceRoles(): Promise<AxiosResponse> {
    try {
      return await Axios.get<IWorkspaceRoleDto, AxiosResponse>(`${backendUrl}/authorization/roles`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async ConfirmWorkspaceOwnershipTransfer(code: string): Promise<AxiosResponse> {
    try {
      return await Axios.put<IAssignWorkspaceRoleToUserRequest, AxiosResponse>(
        `${backendUrl}/authorization/workspace-ownership-transfer`,
        {
          code
        }
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async ChangeDomain(domainName: string) {
    try {
      await Axios.post<null>(`${backendUrl}/users/changeDomain/${domainName}`);
      await UserService.GetUserInfoForce();
      return true;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return false;
    }
  }

  public static async GetUsersByDomainId(): Promise<AxiosResponse> {
    try {
      return await Axios.get<IUserDto, AxiosResponse>(`${backendUrl}/users`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  // Lean directory (id, name, username, photo) for member pickers / avatars —
  // avoids the heavy roles/invitations/count payload of GetUsersByDomainId.
  public static async GetDomainUsersDirectory(): Promise<AxiosResponse> {
    try {
      return await Axios.get<IUserDto, AxiosResponse>(`${backendUrl}/users/directory`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetUserStatus(username): Promise<AxiosResponse> {
    try {
      return await Axios.get<string, AxiosResponse>(`${backendUrl}/users/status?username=${username}`);
    } catch (error: any) {
      return error;
    }
  }

  public static async GetSubscriptionsByDomainId(): Promise<AxiosResponse> {
    try {
      return await Axios.get<ISubscriptionDto, AxiosResponse>(`${backendUrl}/subscriptions/`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetAuthorizedDomains(): Promise<AxiosResponse> {
    try {
      return await Axios.get<IUserDto, AxiosResponse>(`${backendUrl}/users/getAuthorizedDomains`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async Delete(id: string): Promise<boolean> {
    try {
      await Axios.delete<null>(`${backendUrl}/users/${id}`);
      return true;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return false;
    }
  }

  public static async Update(id: string, usr: IUserDto): Promise<AxiosResponse> {
    try {
      return await Axios.put<IUserDto, AxiosResponse>(`${backendUrl}/users/${id}`, usr);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async UpdateProfilePhoto(id: string, file: any): Promise<AxiosResponse> {
    try {
      return await Axios.put<IUserDto, AxiosResponse>(`${backendUrl}/users/${id}/profile-photo`, file);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async RemoveProfilePhoto(id: string): Promise<boolean> {
    try {
      await Axios.delete<null>(`${backendUrl}/users/${id}/profile-photo`);
      return true;
    } catch (error: unknown) {
      KMainFunctions.exceptionHandler(error);
      return false;
    }
  }

  public static async ChangePassword(obj: IChangePasswordDto): Promise<AxiosResponse> {
    try {
      return await Axios.post<IChangePasswordDto, AxiosResponse>(`${backendUrl}/auth/changepassword`, obj);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async VerifyVerificationCode(obj: IVerifyVerificationCodeDto): Promise<AxiosResponse> {
    try {
      return await Axios.post<IChangePasswordDto, AxiosResponse>(`${backendUrl}/auth/isverificationcodevalid`, obj);
    } catch (error: any) {
      return error;
    }
  }

  public static async DeleteInvitation(id: string): Promise<AxiosResponse> {
    try {
      return await Axios.delete<null>(`${backendUrl}/users/invitation/${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async SignupUnknownUser(user: IUserDto): Promise<AxiosResponse> {
    try {
      return await Axios.post<IUserDto, AxiosResponse>(`${backendUrl}/users/signup/unknownuser`, user);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async SendConfirmationEmail(user: string): Promise<AxiosResponse> {
    try {
      return await Axios.post<null>(`${backendUrl}/users/sendconfirmationemail?username=${encodeURIComponent(user)}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async ConfirmEmail(obj: { token: string; username: string }): Promise<AxiosResponse> {
    try {
      const res = await Axios.post<null>(`${backendUrl}/users/confirmemail`, obj);
      if (res && res.status === 200) {
        LocalStorageService.setToken(res.data);
        await this.GetUserInfo();
        const firebase = new FireBaseService();
        firebase.logEvent("K_sign_in", {
          type: "platform",
          username: window.userInfo?.username,
          id: window.userInfo?.id
        });
        firebase.setUserId(window.userInfo?.id);
      }
      return res;
    } catch (error: any) {
      // KMainFunctions.exceptionHandler(error);
      if (error && error.response && error.response.data) {
        return error.response.data.message;
      }
      return error;
    }
  }

  public static async LoginWithExternalToken(externalTokenRequest: IExternalTokenRequest): Promise<AxiosResponse> {
    try {
      const res = await Axios.post<ILoginDto, AxiosResponse>(`${backendUrl}/auth/loginwithtoken`, externalTokenRequest);
      const result = await this.ProcessLoginResult(res);

      const firebase = new FireBaseService();

      firebase.logEvent("K_sign_in", {
        type: "external_token",
        username: window.userInfo?.username,
        id: window.userInfo?.id,
        login_type: externalTokenRequest.providerType
      });
      firebase.setUserId(window.userInfo?.id);
      return result;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async LoginWithGoogle(obj: any): Promise<AxiosResponse> {
    return Axios.post<null, AxiosResponse>(`${backendUrl}/auth/googletokenprovider`, obj);
  }

  public static async GetAllRoles(): Promise<AxiosResponse<IRoleDto[]>> {
    try {
      return await Axios.get<IRoleDto[], AxiosResponse>(`${backendUrl}/auth/roles`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }
}
